import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import { getGuideAccent } from "../../utils/guideAccent";

// Template335 (Edimburgo): Día 3 portada Dean Village, Stockbridge y New Town
const Template335 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const introText = page.texts?.[1]?.content;
	const subtitle = page.texts?.[2]?.content;
	const portadaImage = getImageSrc("portada");
	const heroSticker = getCanvasImageUrl("hero-sticker");
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{portadaImage && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					<img
						src={portadaImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{subtitle && (
						<Box
							component="h1"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								fontSize: isSmallScreen ? "22px" : "30px",
								textAlign: isSmallScreen ? "left" : "center",
								position: "absolute",
								bottom: 0,
								margin: "10px",
								left: "20px",
								zIndex: 3,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{subtitle}
						</Box>
					)}
					{heroSticker && (
						<img
							src={heroSticker}
							alt=""
							style={{
								width: isSmallScreen ? 80 : 90,
								position: "absolute",
								bottom: isSmallScreen ? 50 : 50,
								left: 18,
								zIndex: 4,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				data-block-id="text-1"
				sx={{
					padding: "8px 12px 5px",
					position: "relative",
					marginTop: 0,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				{introText && (
					<Box
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginTop: 0,
							padding: 0,
							"& p": { margin: "0 0 8px" },
							"& p:last-child": { marginBottom: "8px" },
						}}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						overflow: "visible",
					}}
				>
					{sections.map((section, idx) => {
						const imageSrc = getImageSrc(section.imageId);
						const isLast = idx === sections.length - 1;

						if (section.layout === "box" || section.layout === "boxOutline") {
							const isOutline = section.layout === "boxOutline";
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{ margin: 0, padding: 0 }}
								>
									<Box
										component="p"
										sx={{
											...estiloP,
											padding: "12px",
											backgroundColor: isOutline ? "#f7fafc" : accent.boxBg,
											border: isOutline
												? `3px solid ${accent.boxBorder}`
												: "none",
											borderRadius: "12px",
										}}
									>
										{section.title && (
											<Box component="h3" sx={estiloH3}>
												{section.title}
											</Box>
										)}
										{section.body}
									</Box>
								</Box>
							);
						}

						if (section.layout === "list") {
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{ margin: 0, padding: 0 }}
								>
									{section.title && (
										<Box
											component="h3"
											sx={{ ...estiloH3, marginBottom: "6px" }}
										>
											{section.title}
										</Box>
									)}
									<Box
										component="ul"
										sx={{
											...estiloP,
											paddingLeft: "18px",
											margin: 0,
										}}
									>
										{(section.listItems || []).map((item, itemIdx) => (
											<Box
												component="li"
												key={itemIdx}
												sx={{ marginBottom: "6px" }}
											>
												{item.title && <strong>{item.title}. </strong>}
												{item.body}
											</Box>
										))}
									</Box>
								</Box>
							);
						}

						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 2}`}
								sx={{
									display: "flex",
									gap: "16px",
									alignItems: "flex-start",
									position: "relative",
									overflow: "visible",
									margin: 0,
									padding: 0,
								}}
							>
								{imageSrc && (
									<Box
										sx={{
											width: "40%",
											position: "relative",
											flexShrink: 0,
											overflow: "visible",
											lineHeight: 0,
											fontSize: 0,
										}}
									>
										<img
											src={imageSrc}
											alt={section.title}
											style={{
												width: "100%",
												height: 110,
												objectFit: "cover",
												borderRadius: "12px",
												display: "block",
											}}
										/>
										{section.stickerCanvasId &&
											section.stickerCanvasId !== "holyrood-sticker" &&
											getCanvasImageUrl(section.stickerCanvasId) && (
												<>
													<img
														src={getCanvasImageUrl(section.stickerCanvasId)}
														alt=""
														style={{
															width: 95,
															position: "absolute",
															bottom:
																section.stickerCanvasId === "closes-sticker" ||
																section.stickerCanvasId ===
																	"dean-village-sticker-2"
																	? isSmallScreen
																		? -100
																		: -80
																	: -20,
															left: -30,
															zIndex: 5,
														}}
													/>
													{section.bubble && (
														<Box
															sx={{
																position: "absolute",
																left:
																	section.stickerCanvasId ===
																		"closes-sticker" ||
																	section.stickerCanvasId ===
																		"dean-village-sticker-2"
																		? -10
																		: 0,
																top:
																	section.stickerCanvasId ===
																		"closes-sticker" ||
																	section.stickerCanvasId ===
																		"dean-village-sticker-2"
																		? 70
																		: "auto",
																bottom:
																	section.stickerCanvasId ===
																		"closes-sticker" ||
																	section.stickerCanvasId ===
																		"dean-village-sticker-2"
																		? "auto"
																		: 50,
																backgroundColor: "#fff8db",
																border: "2px solid #f5c400",
																borderRadius: "16px 16px 16px 0px",
																padding: "8px 10px",
																fontSize: 11,
																fontFamily: `${
																	fontFamily || "'Poppins', sans-serif"
																}`,
																color: contentColor,
																boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
																width: isSmallScreen ? 130 : 200,
																zIndex: 6,
																lineHeight: 1.35,
															}}
														>
															{section.bubble}
														</Box>
													)}
												</>
											)}
									</Box>
								)}
								{section.sabiasQue &&
									section.sabiasQue.map((sq, i) => (
										<Box
											key={i}
											sx={{
												position: "absolute",
												top: -10 + i * 38,
												left: imageSrc ? "calc(40% - 22px)" : "48%",
											}}
										>
											<SabiasQue
												icono={sq.icono}
												texto={sq.texto}
												position={isLast ? "above" : undefined}
											/>
										</Box>
									))}
								{section.stickerCanvasId === "holyrood-sticker" &&
									getCanvasImageUrl(section.stickerCanvasId) && (
										<>
											<img
												src={getCanvasImageUrl(section.stickerCanvasId)}
												alt=""
												style={{
													width: 95,
													position: "absolute",
													right: -16,
													bottom: -48,
													zIndex: 5,
													pointerEvents: "none",
												}}
											/>
											{section.bubble && (
												<Box
													sx={{
														position: "absolute",
														right: 70,
														bottom: -28,
														backgroundColor: "#fff8db",
														border: "2px solid #f5c400",
														borderRadius: "16px 16px 0px 16px",
														padding: "8px 10px",
														fontSize: 11,
														fontFamily: `${
															fontFamily || "'Poppins', sans-serif"
														}`,
														color: contentColor,
														boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
														width: isSmallScreen ? 130 : 150,
														zIndex: 6,
														lineHeight: 1.35,
													}}
												>
													{section.bubble}
												</Box>
											)}
										</>
									)}
								<Box
									sx={{
										width: imageSrc ? "60%" : "100%",
										boxSizing: "border-box",
										paddingRight: 0,
									}}
								>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box
											component="p"
											sx={{ ...estiloP, fontSize: 13, color: contentColor }}
										>
											{section.body}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}
					{llegar && llegar.title && (
						<Box
							data-block-id="text-llegar"
							sx={{
								backgroundColor: accent.boxBg,
								border: `3px solid ${accent.boxBorder}`,
								borderRadius: "12px",
								padding: "8px 12px",
								fontSize: 12,
								fontFamily: `${
									fontFamily || "'Poppins', sans-serif"
								} !important`,
								color: "#333",
								width: "100%",
								boxSizing: "border-box",
								textAlign: "left",
							}}
						>
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "6px",
									fontSize: 13,
									fontWeight: 700,
									color: accent.accent,
									textTransform: "uppercase",
								}}
							>
								{llegar.title}
							</Box>
							{llegar.content && (
								<Box
									component="div"
									dangerouslySetInnerHTML={{ __html: llegar.content }}
									sx={{
										margin: 0,
										padding: 0,
										lineHeight: 1.5,
										textAlign: "left",
										"& ul": {
											paddingLeft: "16px",
											margin: 0,
											listStylePosition: "outside",
										},
										"& li": { marginBottom: 2 },
									}}
								/>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template335.thumbnail = "/images/template-1.png";

export default Template335;
